import classNames from "classnames";
import { inject, observer } from "mobx-react";
import React from "react";
import { CallTimer } from "@fidget/common/components/call/callTimer";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n, L10nHtml } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { IBreakingPointsInfo } from "@fidget/common/models/breakingPoints";
import { isFullscreenSupported } from "@fidget/common/utils/browserSupport";
import { fullscreen } from "@fidget/common/utils/fullscreen";
import { ForeContext } from "../../context";
import { IForeCallStore } from "../../models/call/foreCallModel";
import { WidgetButton } from "@fidget/common/components/widgetButton/widgetButton";
import styles from "./callingHeader.css";
import { WidgetCallControls } from "./widgetCallControls";
import { WidgetControlCircle } from "./widgetControlCircle";
import { WidgetVideoSection } from "./widgetVideoSection";

interface Props {
  context: ForeContext;
  foreCallStore?: IForeCallStore;
  breakingPoints: IBreakingPointsInfo;
}

@inject("foreCallStore")
@observer
export class CallingHeader extends React.Component<Props, {}> {
  videosContainer: HTMLElement | undefined = undefined;

  constructor(props: Props) {
    super(props);
  }

  get injected(): { foreCallStore: IForeCallStore } {
    return this.props as { foreCallStore: IForeCallStore };
  }

  enterFullscreen = () => {
    fullscreen.request(this.videosContainer);
  };

  render() {
    const call = this.injected.foreCallStore;
    const assignedAgent = this.props.context.state.shared.assignedAgent;
    const { shouldCallDrawerBeHidden, isMobile } = this.props.breakingPoints;

    return (
      <>
        <div
          className={styles["videos-container"]}
          ref={(el) => (this.videosContainer = el || undefined)}
        >
          <WidgetVideoSection context={this.props.context} isMobile={isMobile} />
          <div className={styles["on-video-controls"]}>
            <div className={classNames(styles.expand, styles.cell)}>
              {!isMobile && (call.remoteVideo.isPlayable || call.videoExpanded) && (
                <TooltipComponent
                  text={intl.formatMessage("widget.call.minimise")}
                  maxWidth={`180px`}
                  style={"widget"}
                  positionPreset={"top"}
                >
                  <WidgetControlCircle onClick={call.toggleVideoExpanded}>
                    <i className={"icon-minimise-video-icon"} />
                  </WidgetControlCircle>
                </TooltipComponent>
              )}
            </div>
            <div className={styles.cell}>
              {call.remoteVideo.isPlayable && isFullscreenSupported && (
                <WidgetButton buttonStyle={"secondary"} small onClick={this.enterFullscreen}>
                  <L10n id={"widget.call.fullscreen"} />
                </WidgetButton>
              )}
            </div>
            <div className={styles.cell}>
              <div
                className={classNames(styles["muted-info"], {
                  [styles.hidden]: call.remoteAudio.enabled
                })}
              >
                <i className="icon-crossed-mic" />
                <span className={styles.text}>
                  <L10nHtml
                    id={"widget.call.agentIsMuted"}
                    values={{ name: assignedAgent!.firstName }}
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={styles["call-panel"]}>
          <div className={styles.info}>
            <div className={styles.name}> {assignedAgent!.firstName} </div>
            <div className={styles.timer}>
              {(call.startTimestamp && <CallTimer startTimestamp={call.startTimestamp} />) || (
                <L10n id={"widget.call.connecting"} />
              )}
            </div>
          </div>
          <WidgetCallControls
            shouldCallDrawerBeHidden={shouldCallDrawerBeHidden}
            foreContext={this.props.context}
          />
          <div className={styles.empty} />
        </div>
      </>
    );
  }
}
